import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Users, Clock, Globe, ArrowUpRight } from "lucide-react";
import { fadeUp, staggerParent } from "@/lib/motion";
import { CountUp } from "@/components/motion-primitives";

const VALUES = [
  { icon: Award, title: "Engineering-first", desc: "We staff senior voice engineers — not call-center reps — on every account." },
  { icon: Clock, title: "Stay until it works", desc: "We don't hand off and disappear. Success = your call quality, not our ticket count." },
  { icon: Globe, title: "Vendor-neutral", desc: "We deploy the right platform for your size and goals — hosted, FreePBX, 3CX, Asterisk, hybrid." },
  { icon: Users, title: "Operator empathy", desc: "We've sat at the front desk. Every system we build respects the humans who use it daily." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-24 sm:pt-32 overflow-hidden">
      <section className="relative py-16 sm:py-24 grain">
        <div className="container-x relative">
          <motion.div
            initial="hidden"
            animate="show"
            variants={staggerParent}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10"
          >
            <div className="lg:col-span-8">
              <motion.div variants={fadeUp} className="text-overline">
                About
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="mt-4 font-display text-6xl sm:text-8xl text-ink tracking-[-0.04em] leading-[0.92]"
              >
                Built by voice engineers who'd rather you never had to think about your phone system.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mt-8 text-lg sm:text-xl text-ink-2 leading-relaxed max-w-2xl"
              >
                Mavericks PBX Solutions is a business communications company.
                We design, deploy, and manage PBX phone systems and unified
                communications for organizations that depend on reliable,
                intelligent call handling.
              </motion.p>
              <motion.p
                variants={fadeUp}
                className="mt-4 text-lg text-ink-2 leading-relaxed max-w-2xl"
              >
                Our customers don't think about call routing, voicemail, or
                auto attendants — because we engineer those layers to disappear
                behind a great customer experience.
              </motion.p>
            </div>

            {/* Sticky stats sidebar */}
            <motion.div variants={fadeUp} className="lg:col-span-4">
              <div className="rounded-3xl border border-ink bg-white p-8">
                <div className="text-overline mb-6">Commitments</div>
                <div className="space-y-8">
                  <div>
                    <div className="font-display text-5xl text-ink leading-none">
                      <CountUp value="99.99" suffix="%" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-warm mt-2 font-semibold">
                      Platform uptime SLA
                    </div>
                  </div>
                  <div className="border-t border-ink pt-8">
                    <div className="font-display text-5xl text-ink leading-none">24/7</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-warm mt-2 font-semibold">
                      US-based incident response
                    </div>
                  </div>
                  <div className="border-t border-ink pt-8">
                    <div className="font-display text-5xl text-ink leading-none">
                      ≤ <CountUp value="1" /> day
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-warm mt-2 font-semibold">
                      Quote turnaround
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 sm:py-28 border-t border-ink bg-bone-2">
        <div className="container-x">
          <div className="text-overline">What we value</div>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl text-ink tracking-tight leading-[0.98] max-w-3xl">
            Four principles that shape every deployment.
          </h2>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={staggerParent}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              const tints = ["bg-lime-pop", "bg-coral", "bg-white", "bg-ink text-white"];
              return (
                <motion.div
                  key={v.title}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35 }}
                  className={`${tints[i % tints.length]} rounded-3xl border border-ink p-8`}
                >
                  <div
                    className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      i % 4 === 3 ? "bg-lime-pop text-ink" : "bg-ink text-white"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl sm:text-3xl tracking-tight">
                    {v.title}
                  </h3>
                  <p className={`mt-2 leading-relaxed ${i % 4 === 3 ? "text-white/80" : "text-ink-2"}`}>
                    {v.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-5xl sm:text-6xl text-ink tracking-tight leading-[0.98]">
            Let's talk about your comms stack.
          </h2>
          <p className="mt-5 text-lg text-ink-2">
            Share a few details and we'll respond with a tailored recommendation.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="btn-lime group text-base h-14 px-8"
              data-testid="about-cta-inquire"
            >
              Send an Inquiry
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
